import { Suspense, useEffect, useState } from 'react';
import { Redirect, Switch, useRouteMatch } from 'react-router-dom';
import { Loader } from '@givebutter/primitives/loader';
import * as NavMenu from 'components/nav-menu';
import { AuctionCommunications } from 'components/pages/campaign-settings/campaign-auction/auction-communications';
import { GetStarted } from 'components/pages/campaign-settings/campaign-auction/auction-router/get-started';
import { useAuctionsStore } from 'components/pages/campaign-settings/campaign-auction/auction-router/useAuctionsStore';
import { AuctionSettings } from 'components/pages/campaign-settings/campaign-auction/auction-settings';
import { BiddersRouter } from 'components/pages/campaign-settings/campaign-auction/bidders-router';
import { ItemFulfillment } from 'components/pages/campaign-settings/campaign-auction/item-fulfillment';
import { ItemsRouter } from 'components/pages/campaign-settings/campaign-auction/items-router';
import { SoldItems } from 'components/pages/campaign-settings/campaign-auction/sold-items';
import { Route } from 'components/router/Route';
import { RenderOrRedirect } from 'components/util';
import { IntercomProductTourTargets } from 'constants/intercom';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';

const AuctionRoutes = {
    Settings: 'settings',
    Items: 'items',
    Payments: 'payments',
    Registration: 'registration',
    Notifications: 'notifications',
    Bidders: 'bidders',
    WinningBids: 'winning-bids',
    Fulfillment: 'fulfillment',
};

const AuctionRouter = () => {
    const match = useRouteMatch();
    const { data: campaign } = useCurrentCampaign();
    const [didInit, setDidInit] = useState(false);
    const { showGettingStarted, setShowGettingStarted } = useAuctionsStore();
    const auctionId = campaign.auction_id;

    useEffect(() => {
        if (!didInit) {
            setShowGettingStarted(!auctionId);
            setDidInit(true);
        }
    }, [auctionId, didInit, setShowGettingStarted]);

    if (!didInit) {
        return null;
    }

    return (
        <div className={'grid grid-cols-10 gap-4'}>
            {!showGettingStarted && (
                <NavMenu.Root
                    orientation="vertical"
                    className="col-span-full lg:col-span-2"
                    aria-label="Auction"
                >
                    <NavMenu.Item
                        data-intercom-target={IntercomProductTourTargets.AuctionSettingsSubnav}
                        exact={true}
                        to={`${match.url}`}
                    >
                        Auction settings
                    </NavMenu.Item>
                    <NavMenu.Item
                        data-intercom-target={IntercomProductTourTargets.AuctionNotificationsSubnav}
                        exact={true}
                        to={`${match.url}/${AuctionRoutes.Notifications}`}
                        disabled={!campaign.auction_id}
                    >
                        Notifications
                    </NavMenu.Item>
                    <NavMenu.Item
                        data-intercom-target={IntercomProductTourTargets.AuctionItemsSubnav}
                        to={`${match.url}/${AuctionRoutes.Items}`}
                        disabled={!campaign.auction_id}
                    >
                        Items
                    </NavMenu.Item>
                    <NavMenu.Item
                        data-intercom-target={IntercomProductTourTargets.AuctionBiddersSubnav}
                        to={`${match.url}/${AuctionRoutes.Bidders}`}
                        disabled={!campaign.auction_id}
                    >
                        Bidders
                    </NavMenu.Item>
                    <NavMenu.Item
                        data-intercom-target={IntercomProductTourTargets.AuctionWinningBidsSubnav}
                        to={`${match.url}/${AuctionRoutes.WinningBids}`}
                        disabled={!campaign.auction_id}
                    >
                        Winning bids
                    </NavMenu.Item>
                    <NavMenu.Item
                        data-intercom-target={IntercomProductTourTargets.AuctionFulfillmentSubnav}
                        to={`${match.url}/${AuctionRoutes.Fulfillment}`}
                        disabled={!campaign.auction_id}
                    >
                        Item fulfillment
                    </NavMenu.Item>
                </NavMenu.Root>
            )}
            <div className={`col-span-full ${!showGettingStarted ? 'lg:col-span-8' : ''}`}>
                <Suspense
                    fallback={
                        <div className={'flex min-h-52 items-center justify-center'}>
                            <Loader variant={'static'} />
                        </div>
                    }
                >
                    <Switch>
                        <Route exact={true} path={match.path}>
                            {showGettingStarted ? <GetStarted /> : <AuctionSettings />}
                        </Route>
                        <Route exact={true} path={`${match.path}/${AuctionRoutes.Notifications}`}>
                            <RenderOrRedirect cond={!!campaign.auction_id} redirectTo={match.url}>
                                <AuctionCommunications />
                            </RenderOrRedirect>
                        </Route>
                        <Route path={`${match.path}/${AuctionRoutes.Items}`}>
                            <RenderOrRedirect cond={!!campaign.auction_id} redirectTo={match.url}>
                                <ItemsRouter />
                            </RenderOrRedirect>
                        </Route>
                        <Route path={`${match.path}/${AuctionRoutes.Bidders}`}>
                            <RenderOrRedirect cond={!!campaign.auction_id} redirectTo={match.url}>
                                <BiddersRouter />
                            </RenderOrRedirect>
                        </Route>
                        <Route exact={true} path={`${match.path}/${AuctionRoutes.WinningBids}`}>
                            <RenderOrRedirect cond={!!campaign.auction_id} redirectTo={match.url}>
                                <SoldItems />
                            </RenderOrRedirect>
                        </Route>
                        <Route exact={true} path={`${match.path}/${AuctionRoutes.Fulfillment}`}>
                            <RenderOrRedirect cond={!!campaign.auction_id} redirectTo={match.url}>
                                <ItemFulfillment />
                            </RenderOrRedirect>
                        </Route>
                        <Route>
                            <Redirect to={match.url} />
                        </Route>
                    </Switch>
                </Suspense>
            </div>
        </div>
    );
};

export { AuctionRouter };
